import { useAtom, useAtomValue } from 'jotai';
import React, { Activity, useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';

import { DrawerEvents, EmbeddedDrawer } from '@crossnokaye/ux-common/ckds';

import { EquipmentBulkEditDrawer } from './equipment-bulk-edit-drawer';
import { EquipmentDetailsDrawer } from './equipment-details-drawer';
import { drawerIsOpenAtom, DrawerType, drawerTypeAtom } from './state/drawer.state';
import { useOpenBulkEdit } from '../equipment-bulk-edit/hooks/use-open-bulk-edit';
import { pushOutOpenAtom } from '../equipment-bulk-edit/state/bulk-edit-params.state';
import { useEquipmentDetailsRouteState } from '../equipment-details/hooks/use-equipment-details-route-state';
import { useOpenResetFaulted } from '../reset-faulted/hooks/use-open-reset-faulted';
import { ResetFaultedView } from '../reset-faulted/reset-faulted-view';

/** Drawer types opened through the Jotai drawer atoms (details is driven by the route). */
const ATOM_DRAWER_TYPES: ReadonlyArray<DrawerType> = ['bulk-edit', 'reset-faulted'];

type EquipmentDrawerLoaderProps = {
  children?: React.ReactNode;
};

export const EquipmentDrawerLoader = ({
  children,
}: EquipmentDrawerLoaderProps): React.ReactNode => {
  const { closeEquipmentDetails, isDetailsOpen } = useEquipmentDetailsRouteState();
  const [isAtomDrawerOpen, setIsAtomDrawerOpen] = useAtom(drawerIsOpenAtom);
  const drawerType = useAtomValue(drawerTypeAtom);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(isDetailsOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleDrawerType, setVisibleDrawerType] = useState<DrawerType | undefined>(undefined);
  const closingDrawerTypeRef = useRef<DrawerType | undefined>(undefined);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const previousPathnameRef = useRef(pathname);

  const isPushOutOpen = useAtomValue(pushOutOpenAtom);
  const { clearBulkEditAction } = useOpenBulkEdit();
  const { clearResetFaultedAction } = useOpenResetFaulted();

  useEffect(() => {
    setDetailsDrawerOpen(isDetailsOpen);
  }, [isDetailsOpen]);

  useEffect(() => {
    if (!detailsDrawerOpen || !isAtomDrawerOpen) return;

    if (drawerType === 'bulk-edit') {
      clearBulkEditAction();
    } else if (drawerType === 'reset-faulted') {
      clearResetFaultedAction();
    }
  }, [
    detailsDrawerOpen,
    isAtomDrawerOpen,
    drawerType,
    clearBulkEditAction,
    clearResetFaultedAction,
  ]);

  // The loader wraps every route, so atom drawers would otherwise stay open (and keep
  // hiding the page) after navigating elsewhere. Details closes itself: its state is
  // in the route search, which the new route drops.
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (isAtomDrawerOpen) {
      setIsAtomDrawerOpen(false);
    }
  }, [isAtomDrawerOpen, pathname, setIsAtomDrawerOpen]);

  const activeDrawerType: DrawerType | undefined = detailsDrawerOpen
    ? 'details'
    : isAtomDrawerOpen && drawerType && ATOM_DRAWER_TYPES.includes(drawerType)
      ? drawerType
      : undefined;
  const drawerIsControlledOpen = Boolean(activeDrawerType);
  const isDrawerMounted = drawerIsControlledOpen || isVisible;
  const renderedDrawerType = activeDrawerType ?? visibleDrawerType;

  const closeDrawerAction = () => {
    closingDrawerTypeRef.current = activeDrawerType;
    if (activeDrawerType === 'details') {
      setDetailsDrawerOpen(false);
      void closeEquipmentDetails();
      return;
    }

    if (activeDrawerType && ATOM_DRAWER_TYPES.includes(activeDrawerType)) {
      setIsAtomDrawerOpen(false);
    }
  };

  useEffect(() => {
    if (activeDrawerType) {
      setIsVisible(true);
      setVisibleDrawerType(activeDrawerType);
    }
  }, [activeDrawerType]);

  const handleDrawerClose = (event: (typeof DrawerEvents)[keyof typeof DrawerEvents]) => {
    if (event === DrawerEvents.CLOSE_COMPLETE) {
      const closingDrawerType = closingDrawerTypeRef.current ?? visibleDrawerType;
      setIsVisible(false);
      setVisibleDrawerType(undefined);
      closingDrawerTypeRef.current = undefined;

      if (closingDrawerType === 'bulk-edit') {
        clearBulkEditAction();
      } else if (closingDrawerType === 'reset-faulted') {
        clearResetFaultedAction();
      }
    }
  };

  return (
    <>
      <Activity mode={isDrawerMounted ? 'visible' : 'hidden'}>
        <EmbeddedDrawer
          open={drawerIsControlledOpen}
          options={{ closeOnEscape: !isPushOutOpen }}
          onClose={closeDrawerAction}
          onDrawerEvent={handleDrawerClose}
        >
          {renderedDrawerType === 'details' && (
            <EquipmentDetailsDrawer onClose={closeDrawerAction} />
          )}
          {renderedDrawerType === 'bulk-edit' && (
            <EquipmentBulkEditDrawer onClose={async () => closeDrawerAction()} />
          )}
          {renderedDrawerType === 'reset-faulted' && (
            <ResetFaultedView onClose={closeDrawerAction} />
          )}
        </EmbeddedDrawer>
      </Activity>

      <Activity mode={isDrawerMounted ? 'hidden' : 'visible'}>{children}</Activity>
    </>
  );
};
